// Lomi Live — celebration FX. Mirrors the 10k-milestone "live wall" feel:
// Ethiopian-flag + lime + lemon confetti, fireworks for winners, a sparkle bloom.
//
// canvas-confetti is loaded lazily from a CDN (no package.json/lock churn). If it
// can't load (offline), every call degrades to a silent no-op — never blocks the UI.

type ConfettiFn = (opts: Record<string, unknown>) => void;

const CDN = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.2/dist/confetti.browser.min.js';

// Lomi citrus + Ethiopian flag (green/yellow/red) — the historic, festive mix.
const LIME = ['#84cc16', '#22c55e', '#a3e635', '#bef264', '#65a30d'];
const LEMON = ['#facc15', '#fde047', '#fbbf24', '#a3e635', '#84cc16'];
const FLAG = ['#078930', '#1f9e4a', '#FCDD09', '#ffe44d', '#DA121A', '#ff5560', '#a3e635'];

let loading: Promise<ConfettiFn | null> | null = null;

function load(): Promise<ConfettiFn | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const w = window as unknown as { confetti?: ConfettiFn };
  if (w.confetti) return Promise.resolve(w.confetti);
  if (loading) return loading;
  loading = new Promise((resolve) => {
    try {
      const s = document.createElement('script');
      s.src = CDN;
      s.async = true;
      s.onload = () => resolve(w.confetti || null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    } catch {
      resolve(null);
    }
  });
  return loading;
}

const reduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A celebratory burst from the lower-center — fire on a result / good moment. */
export async function liveCelebrate(palette: 'lime' | 'lemon' = 'lemon'): Promise<void> {
  if (reduced()) return;
  const confetti = await load();
  if (!confetti) return;
  const colors = palette === 'lime' ? LIME : LEMON;
  confetti({ particleCount: 90, spread: 78, startVelocity: 42, gravity: 1.05, ticks: 220, origin: { x: 0.5, y: 0.6 }, colors, scalar: 1.05 });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 70, origin: { x: 0, y: 0.65 }, colors, ticks: 240 }), 180);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 70, origin: { x: 1, y: 0.65 }, colors, ticks: 240 }), 180);
}

/** Bigger, flag-coloured fireworks — for champions at the reveal. ~2.5s. */
export async function liveFireworks(durationMs = 2600): Promise<void> {
  if (reduced()) return;
  const confetti = await load();
  if (!confetti) return;
  const end = Date.now() + durationMs;
  const shell = () => {
    const x = 0.15 + Math.random() * 0.7;
    const y = 0.2 + Math.random() * 0.3;
    confetti({ particleCount: 120, spread: 360, startVelocity: 50, gravity: 1.15, ticks: 240, origin: { x, y }, colors: FLAG, scalar: 1.1 });
  };
  shell();
  const iv = setInterval(() => {
    if (Date.now() > end) {
      clearInterval(iv);
      return;
    }
    shell();
  }, 420);
}

/** Gentle one-time flag sprinkle when a keepsake card opens (feels alive). */
export async function liveCardSparkle(): Promise<void> {
  if (reduced()) return;
  const confetti = await load();
  if (!confetti) return;
  confetti({ particleCount: 70, spread: 100, startVelocity: 30, gravity: 0.9, ticks: 260, origin: { x: 0.5, y: 0.35 }, colors: FLAG, scalar: 1.05 });
}
